/**
 * Custom Collection Template
 * Handles filtering by metafields and inline variant selection
 */

class CustomCollectionTemplate {
  constructor() {
    this.currentFilter = 'all';
    this.currentFilterButton = null;
    this.productCards = [];
    this.hasLoadedInitially = false;
    this.originalProducts = null;
    this.currentView = 'products'; // 'products' or 'variants'
    this.currentProduct = null;
    this.columnsPerRow = 3;
    this.mapNoticeEnabled = false;
    this.mapInlineMessage = '';
    // Comparison state
    this.selectedProducts = [];
    this.MAX_COMPARISON_PRODUCTS = 3;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const collectionSection = document.querySelector('[data-current-collection]');
    if (collectionSection) {
      this.mapNoticeEnabled = collectionSection.getAttribute('data-map-notice-enabled') === 'true';
      this.mapInlineMessage = collectionSection.getAttribute('data-map-inline-message') || '';
    }

    // Cache all product cards
    this.productCards = Array.from(document.querySelectorAll('.product-card'));
    
    // Store original product data for sorting
    this.storeOriginalProductData();
    
    // Debug: Log collection metafield data
    this.debugCollectionMetafields();
    
    // Mark as initially loaded
    this.hasLoadedInitially = true;
    const productGrid = document.querySelector('.product-grid');
    if (productGrid) {
      // Remove any loading classes and show products immediately
      productGrid.classList.remove('collection-loading');
      productGrid.style.display = '';
      productGrid.style.opacity = '1';
      this.columnsPerRow = parseInt(productGrid.getAttribute('data-columns-desktop')) || 3;
      // Store original products HTML
      this.originalProducts = productGrid.innerHTML;
    }
    
    // Prevent any form submissions that might cause page refresh
    this.preventFormSubmissions();
    
    // Bind filter buttons
    this.bindFilterButtons();

    // Mobile filter panel: collapsed-by-default toggle, auto-closes after a pick
    this.bindMobileFilterToggle();

    // Handle filter button text overflow
    this.handleFilterButtonTextOverflow();

    // Bind product selection buttons
    this.bindProductSelectionButtons();
    
    // Bind back button
    this.bindBackButton();
    
    // Bind sort dropdown
    this.bindSortDropdown();
    
    // Initialize sort from URL
    this.initializeSortFromURL();
    
    // Initialize comparison features
    this.initializeComparison();
    
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

  debugCollectionMetafields() {
    // Get the collection section element
    const collectionSection = document.querySelector('[data-current-collection]');
    if (collectionSection) {
      const collectionHandle = collectionSection.getAttribute('data-current-collection');
      console.log('=== Collection Debug Info ===');
      console.log('Collection Handle:', collectionHandle);
      
      // Log all filter buttons to see what brands are available
      const filterButtons = document.querySelectorAll('.collection-filter-btn[data-filter-type="brand"]');
      console.log('Brand Filter Buttons Found:', filterButtons.length);
      
      filterButtons.forEach((button, index) => {
        const brandValue = button.getAttribute('data-filter-value');
        const brandText = button.textContent.trim();
        console.log(`Brand ${index + 1}:`, {
          text: brandText,
          dataValue: brandValue,
          element: button
        });
      });
      
      // Log product brand data
      const productCards = document.querySelectorAll('.product-card[data-product-brand]');
      console.log('Products with brand data:', productCards.length);
      
      productCards.forEach((card, index) => {
        const productBrand = card.getAttribute('data-product-brand');
        const productId = card.getAttribute('data-product-id');
        const productHandle = card.getAttribute('data-product-handle');
        console.log(`Product ${index + 1}:`, {
          id: productId,
          handle: productHandle,
          brand: productBrand
        });
      });
      
      console.log('=== End Collection Debug Info ===');
    }
  }

  preventFormSubmissions() {
    // Prevent any form submissions that might interfere with our AJAX
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.querySelector('.sort_by')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });
  }

  bindFilterButtons() {
    const buttons = document.querySelectorAll('.collection-filter-btn');
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        // If in variant view, go back first
        if (this.currentView === 'variants') {
          this.showProducts();
        }
        
        const filterType = button.getAttribute('data-filter-type');
        const filterValue = button.getAttribute('data-filter-value');
        this.applyFilter(filterType, filterValue, button);
      });
    });
  }

  // Mobile-only collapsible filter panel. CSS defaults the panel to closed
  // inside its own max-width:768px media query (not a class JS has to add),
  // so there's no flash-of-open-content race with this script; on desktop
  // that same media query never applies, so .is-open is a no-op there
  // regardless of state.
  bindMobileFilterToggle() {
    const toggleBtn = document.getElementById('filterToggleBtn');
    const nav = document.getElementById('collectionFilterNav');
    const activeLabelEl = document.getElementById('filterToggleActiveLabel');
    if (!toggleBtn || !nav) return;

    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      toggleBtn.setAttribute('aria-expanded', String(open));
    };

    const updateActiveLabel = () => {
      if (!activeLabelEl) return;
      const active = nav.querySelector('.collection-filter-btn.active') || this.currentFilterButton;
      activeLabelEl.textContent = active ? active.getAttribute('data-original-text') || active.textContent.trim() : '';
    };

    // Sync aria-expanded (and .is-open, harmless either way on mobile since
    // the CSS default already renders closed) to match reality on load.
    setOpen(!isMobile());
    updateActiveLabel();

    toggleBtn.addEventListener('click', () => {
      setOpen(!nav.classList.contains('is-open'));
    });

    // Picking a filter answers the question the panel was open for — close it
    // and let the (now-filtered) grid take over the screen. Desktop is a no-op
    // since setOpen() only has a visible effect inside the mobile media query.
    nav.querySelectorAll('.collection-filter-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        updateActiveLabel();
        if (isMobile()) {
          window.setTimeout(() => setOpen(false), 150);
        }
      });
    });

    // Crossing the breakpoint (rotation, resize) should re-sync to the
    // always-open desktop state rather than staying stuck collapsed.
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!isMobile()) setOpen(true);
      }, 150);
    });
  }

  handleFilterButtonTextOverflow() {
    const buttons = document.querySelectorAll('.collection-filter-btn');
    
    buttons.forEach(button => {
      // Store original text
      if (!button.hasAttribute('data-original-text')) {
        button.setAttribute('data-original-text', button.textContent.trim());
      }
      
      this.adjustButtonText(button);
    });
    
    // Add resize listener to handle dynamic text adjustment
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        buttons.forEach(button => {
          this.adjustButtonText(button);
        });
      }, 100);
    });
  }

  adjustButtonText(button) {
    const originalText = button.getAttribute('data-original-text');
    const container = button.closest('.collection-filter-nav');
    
    if (!originalText || !container) return;
    
    // Reset to original text first
    button.textContent = originalText;
    
    // Check if text is overflowing
    const isOverflowing = button.scrollWidth > button.clientWidth || 
                         button.scrollWidth > container.clientWidth;
    
    if (isOverflowing) {
      const filterType = button.getAttribute('data-filter-type');
      
      if (filterType === 'all' && originalText.startsWith('All - ')) {
        // For "All" button, try shortening to just "All"
        button.textContent = 'All';
        
        // If still overflowing, use ellipsis
        if (button.scrollWidth > button.clientWidth) {
          button.style.textOverflow = 'ellipsis';
          button.style.overflow = 'hidden';
          button.style.whiteSpace = 'nowrap';
        }
      } else {
        // For other buttons, use ellipsis
        button.style.textOverflow = 'ellipsis';
        button.style.overflow = 'hidden';
        button.style.whiteSpace = 'nowrap';
        
        // If still too long, try to truncate at word boundaries first
        let words = originalText.split(' ');
        while (button.scrollWidth > button.clientWidth && words.length > 1) {
          words.pop();
          button.textContent = words.join(' ') + '...';
        }

        // Char-level fallback — always slice from the ORIGINAL text with a
        // shrinking length, never from the already-truncated textContent.
        // (Re-slicing textContent itself here would append "..." faster than
        // it removes characters, growing the string forever and hanging the tab.)
        if (button.scrollWidth > button.clientWidth) {
          let keep = words[0] ? words[0].length : originalText.length;
          while (button.scrollWidth > button.clientWidth && keep > 3) {
            keep -= 1;
            button.textContent = originalText.slice(0, keep) + '...';
          }
        }
      }
    } else {
      // Reset styles if not overflowing
      button.style.textOverflow = '';
      button.style.overflow = '';
      button.style.whiteSpace = '';
    }
  }

  applyFilter(filterType, filterValue, clickedButton) {
    console.log('Applying filter:', filterType, filterValue);
    
    // Don't clear comparison selections when filtering - keep them
    
    // Update active button
    document.querySelectorAll('.collection-filter-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    clickedButton.classList.add('active');
    
    // Store current filter state
    this.currentFilter = filterType;
    this.currentFilterButton = clickedButton;

    let filteredCards = [];
    let hiddenCards = [];

    if (filterType === 'all') {
      // Show all products in original order
      filteredCards = [...this.productCards];
    } else if (filterType === 'product_type') {
      this.productCards.forEach(card => {
        const cardProductType = card.getAttribute('data-product-type');
        const shouldShow = cardProductType && cardProductType.toLowerCase() === filterValue.toLowerCase();
        if (shouldShow) {
          filteredCards.push(card);
        } else {
          hiddenCards.push(card);
        }
      });
    } else if (filterType === 'security_level') {
      // Collect all products with security levels and sort by level (lowest to highest)
      const securityProducts = [];
      this.productCards.forEach(card => {
        const hasSecurity = card.getAttribute('data-has-security-level') === 'true';
        if (hasSecurity) {
          const securityValue = card.getAttribute('data-security-level') || '0';
          const securityLevel = this.extractSecurityLevel(securityValue).level;
          securityProducts.push({ card, level: securityLevel });
        } else {
          hiddenCards.push(card);
        }
      });
      
      // Sort by security level (lowest to highest)
      securityProducts.sort((a, b) => a.level - b.level);
      filteredCards = securityProducts.map(item => item.card);
      
    } else if (filterType === 'fire_rating') {
      // Collect all products with fire ratings and sort by rating (lowest to highest)
      const fireProducts = [];
      this.productCards.forEach(card => {
        const hasFire = card.getAttribute('data-has-fire-rating') === 'true';
        if (hasFire) {
          const fireValue = parseInt(card.getAttribute('data-fire-rating')) || 0;
          fireProducts.push({ card, rating: fireValue });
        } else {
          hiddenCards.push(card);
        }
      });
      
      // Sort by fire rating (lowest to highest)
      fireProducts.sort((a, b) => a.rating - b.rating);
      filteredCards = fireProducts.map(item => item.card);
      
    } else if (filterType === 'brand') {
      this.productCards.forEach(card => {
        const cardBrand = card.getAttribute('data-product-brand');
        let shouldShow = false;
        if (cardBrand) {
          // Normalize both brands for comparison (trim whitespace, lowercase)
          const normalizedCardBrand = cardBrand.trim().toLowerCase();
          const normalizedFilterBrand = filterValue.trim().toLowerCase();
          shouldShow = normalizedCardBrand === normalizedFilterBrand;
          console.log(`Product brand: "${cardBrand}", Filter brand: "${filterValue}", Match: ${shouldShow}`);
        }
        if (shouldShow) {
          filteredCards.push(card);
        } else {
          hiddenCards.push(card);
        }
      });
    }

    // Reorder the DOM to show filtered cards first, then hide others
    const productGrid = document.getElementById('productGrid');
    if (productGrid) {
      // Create document fragment for efficient DOM manipulation
      const fragment = document.createDocumentFragment();

      // Add filtered cards
      filteredCards.forEach(card => {
        card.style.display = '';
        card.style.animation = 'fadeIn 0.5s ease-out';
        fragment.appendChild(card);
      });

      // Hide cards that don't match the filter
      hiddenCards.forEach(card => {
        card.style.display = 'none';
      });

      // Clear and repopulate the grid. Column count/width is handled entirely by the
      // auto-fit CSS grid (see sm-collection-template.css) — a short result set naturally
      // stretches to fill the row instead of leaving empty unpopulated columns, with no
      // inline style needed here.
      productGrid.innerHTML = '';
      productGrid.appendChild(fragment);
    }

    // Check if any products are visible
    const visibleCards = this.productCards.filter(card => card.style.display !== 'none');
    const emptyState = document.querySelector('.collection-empty');
    
    if (visibleCards.length === 0) {
      if (!emptyState) {
        const emptyHTML = `
          <div class="collection-empty">
            <h3>No products found</h3>
            <p>No products match the selected filter.</p>
          </div>
        `;
        productGrid.insertAdjacentHTML('afterend', emptyHTML);
      }
      productGrid.style.display = 'none';
    } else {
      productGrid.style.display = '';
      const dynamicEmptyState = productGrid.parentElement.querySelector('.collection-empty:not(:first-child)');
      if (dynamicEmptyState) {
        dynamicEmptyState.remove();
      }
    }
  }

  bindProductSelectionButtons() {
    const buttons = document.querySelectorAll('[data-product-select]');
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Clear comparison selections and hide toast when viewing sizes
        this.resetComparison();
        
        const productCard = button.closest('.product-card');
        const productId = button.getAttribute('data-product-select');
        const productHandle = productCard.getAttribute('data-product-handle');
        const sizeCount = parseInt(button.getAttribute('data-size-count')) || 0;
        const isConfigurator = productCard.getAttribute('data-configurator') === 'true';

        // Check if product has multiple sizes
        if (isConfigurator && sizeCount > 1) {
          // AMSEC/Liberty configurator product — sizes come from model_size_configuration
          // metaobjects (embedded on the card), not real Shopify variants
          this.showConfiguratorSizeSelection(productHandle, productCard);
        } else if (sizeCount > 1) {
          // Show variant selection inline
          this.showVariantSelection(productId, productHandle, productCard);
        } else {
          // Single variant - go directly to product page
          window.location.href = `/products/${productHandle}`;
        }
      });
    });

    // Make entire product cards clickable
    this.productCards.forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking compare button
        if (e.target.closest('.compare-button')) {
          return;
        }

        // Clear comparison selections and hide toast when viewing sizes
        this.resetComparison();

        const productHandle = card.getAttribute('data-product-handle');
        const sizeCount = parseInt(card.getAttribute('data-size-count')) || 0;
        const isConfigurator = card.getAttribute('data-configurator') === 'true';

        if (isConfigurator && sizeCount > 1) {
          this.showConfiguratorSizeSelection(productHandle, card);
        } else if (sizeCount > 1) {
          const productId = card.getAttribute('data-product-id');
          this.showVariantSelection(productId, productHandle, card);
        } else {
          window.location.href = `/products/${productHandle}`;
        }
      });
    });
  }

  bindBackButton() {
    const backButton = document.getElementById('backButton');
    if (backButton) {
      backButton.addEventListener('click', () => {
        this.showProducts();
      });
    }
  }

  bindSortDropdown() {
    const sortSelect = document.getElementById('sort-by');
    if (sortSelect) {
      // Set the current sort value
      const currentSort = sortSelect.getAttribute('data-default-sort');
      if (currentSort) {
        sortSelect.value = currentSort;
        console.log('Set initial sort value to:', currentSort);
      }
      
      // Ensure the selected option is visible
      this.ensureDropdownVisibility(sortSelect);
      
      sortSelect.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sortValue = e.target.value;
        console.log('Sort dropdown changed to:', sortValue);
        this.handleClientSideSort(sortValue);
        return false;
      });
    }
  }

  ensureDropdownVisibility(selectElement) {
    // Force the select to show the selected option with stronger styling
    selectElement.style.color = '#000000';
    selectElement.style.fontWeight = '600';
    selectElement.style.webkitTextFillColor = '#000000';
    selectElement.style.textShadow = 'none';
    
    // Ensure all options are visible
    const options = selectElement.querySelectorAll('option');
    options.forEach(option => {
      option.style.color = '#000000';
      option.style.backgroundColor = '#ffffff';
      option.style.fontWeight = '500';
    });
    
    // Force a reflow to ensure styles are applied
    selectElement.offsetHeight;
    
    console.log('Dropdown visibility ensured, selected value:', selectElement.value);
  }

  handleClientSideSort(sortValue) {
    console.log('Client-side sort triggered:', sortValue);
    
    // If in variant view, go back to products first
    if (this.currentView === 'variants') {
      this.showProducts();
    }
    
    // Show loading state
    this.showSortLoading();
    
    // Add a longer delay to ensure skeleton is visible
    setTimeout(() => {
      this.sortProductsClientSide(sortValue);
    }, 300);
  }

  sortProductsClientSide(sortType) {
    if (!this.allProducts || this.allProducts.length === 0) {
      console.warn('No products to sort');
      this.hideSortLoading();
      return;
    }

    console.log(`Sorting ${this.allProducts.length} products by: ${sortType}`);
    
    let sortedProducts = [...this.allProducts];

    switch (sortType) {
      case 'price-ascending':
        sortedProducts.sort((a, b) => {
          if (a.price === b.price) return a.originalIndex - b.originalIndex;
          return a.price - b.price;
        });
        break;
      case 'price-descending':
        sortedProducts.sort((a, b) => {
          if (a.price === b.price) return a.originalIndex - b.originalIndex;
          return b.price - a.price;
        });
        break;
      case 'title-ascending':
        sortedProducts.sort((a, b) => {
          const comparison = a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' });
          return comparison !== 0 ? comparison : a.originalIndex - b.originalIndex;
        });
        break;
      case 'title-descending':
        sortedProducts.sort((a, b) => {
          const comparison = b.title.localeCompare(a.title, undefined, { numeric: true, sensitivity: 'base' });
          return comparison !== 0 ? comparison : a.originalIndex - b.originalIndex;
        });
        break;
      case 'created-descending':
        sortedProducts.sort((a, b) => {
          const timeDiff = b.createdAt.getTime() - a.createdAt.getTime();
          return timeDiff !== 0 ? timeDiff : a.originalIndex - b.originalIndex;
        });
        break;
      case 'created-ascending':
        sortedProducts.sort((a, b) => {
          const timeDiff = a.createdAt.getTime() - b.createdAt.getTime();
          return timeDiff !== 0 ? timeDiff : a.originalIndex - b.originalIndex;
        });
        break;
      case 'best-selling':
        sortedProducts.sort((a, b) => {
          if (a.bestSelling === b.bestSelling) return a.originalIndex - b.originalIndex;
          return b.bestSelling - a.bestSelling;
        });
        break;
      case 'manual':
      default:
        sortedProducts.sort((a, b) => a.originalIndex - b.originalIndex);
        break;
    }

    console.log(`Sorted ${sortedProducts.length} products`);
    
    // Reorder DOM elements
    this.reorderProductElements(sortedProducts);
    
    // Hide loading and show products with a longer delay to ensure skeleton is visible
    setTimeout(() => {
      this.hideSortLoading();
      // Re-bind product buttons
      this.productCards = Array.from(document.querySelectorAll('.product-card'));
      this.bindProductSelectionButtons();
      console.log('Sorting complete - products should be visible now');
    }, 500);
    
    // Update URL without page reload
    this.updateSortURL(sortType);
  }

  showSortLoading() {
    const productGrid = document.getElementById('productGrid');
    if (productGrid) {
      console.log('Showing sort loading state');
      // Remove loading class first
      productGrid.classList.remove('collection-loading');
      // Set skeleton content first
      productGrid.innerHTML = this.createLoadingHTML();
      // Add loading class to trigger the overlay
      productGrid.classList.add('collection-loading');
    }
  }

  hideSortLoading() {
    const productGrid = document.getElementById('productGrid');
    if (productGrid) {
      console.log('Hiding sort loading state');
      
      // Add fade-in animation to products
      productGrid.style.opacity = '0';
      productGrid.style.transition = 'opacity 0.3s ease';
      
      // Remove loading class with a slight delay for smooth transition
      setTimeout(() => {
        productGrid.classList.remove('collection-loading');
        
        // Force a reflow to ensure the ::after pseudo-element is removed
        productGrid.offsetHeight;
        
        // Fade in the products
        productGrid.style.opacity = '1';
        
        console.log('Product grid classes after removing loading:', productGrid.className);
        console.log('Product grid children count:', productGrid.children.length);
        
        // Make sure the grid is visible
        productGrid.style.display = '';
        
        // Additional fallback - ensure any skeleton content is removed
        const skeletonElements = productGrid.querySelectorAll('.skeleton, .skeleton-card');
        skeletonElements.forEach(el => el.remove());
      }, 100);
    }
  }

  storeOriginalProductData() {
    const productCards = document.querySelectorAll('.product-card');
    this.allProducts = [];
    
    productCards.forEach((card, index) => {
      const titleElement = card.querySelector('.product-series-title');
      const priceElement = card.querySelector('.product-price-range');
      const brandElement = card.querySelector('.product-brand');
      
      // Extract price from text
      let price = 0;
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
        price = parseFloat(priceText) || 0;
      }
      
      // Simulate creation dates and best selling data
      let createdDate = new Date(Date.now() - (index * 24 * 60 * 60 * 1000));
      let bestSelling = Math.floor(Math.random() * 100) + (100 - index);
      
      this.allProducts.push({
        element: card,
        title: titleElement ? titleElement.textContent.trim() : '',
        price: price,
        brand: brandElement ? brandElement.textContent.trim() : '',
        originalIndex: index,
        createdAt: createdDate,
        bestSelling: bestSelling
      });
    });
    
    console.log('Stored product data for sorting:', this.allProducts.length, 'products');
  }

  reorderProductElements(sortedProducts) {
    const productGrid = document.getElementById('productGrid');
    if (!productGrid) {
      console.error('Product grid not found');
      return;
    }

    console.log('Before reordering - Product grid children:', productGrid.children.length);
    console.log('Sorted products to add:', sortedProducts.length);

    // Create document fragment for efficient DOM manipulation
    const fragment = document.createDocumentFragment();
    
    // Add sorted product elements
    sortedProducts.forEach((product, index) => {
      if (product.element) {
        // Clone the element to avoid issues with existing DOM references
        const clonedElement = product.element.cloneNode(true);
        fragment.appendChild(clonedElement);
        console.log(`Added product ${index + 1}:`, product.title);
      }
    });
    
    // Replace the grid content
    productGrid.innerHTML = '';
    productGrid.appendChild(fragment);
    
    console.log('After reordering - Product grid children:', productGrid.children.length);
    console.log('Reordered product elements in DOM');
  }

  updateSortURL(sortValue) {
    try {
      const url = new URL(window.location);
      if (sortValue === 'manual') {
        url.searchParams.delete('sort_by');
      } else {
        url.searchParams.set('sort_by', sortValue);
      }
      window.history.replaceState({}, '', url);
    } catch (error) {
      console.warn('Could not update URL:', error);
    }
  }

  initializeSortFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentSort = urlParams.get('sort_by');
    const sortSelect = document.getElementById('sort-by');
    
    if (currentSort && sortSelect) {
      sortSelect.value = currentSort;
      // Ensure visibility after setting value
      this.ensureDropdownVisibility(sortSelect);
      // Apply the sort if it's not the default
      if (currentSort !== 'manual') {
        this.sortProductsClientSide(currentSort);
      }
    }
  }

  showProducts() {
    const productGrid = document.getElementById('productGrid');
    const collectionTitleText = document.getElementById('collectionTitleText');
    const backButtonContainer = document.getElementById('backButtonContainer');
    const sizeSubtitle = document.getElementById('sizeSelectionSubtitle');
    const sortContainer = document.querySelector('.collection-sort-container');
    
    // Restore original products
    if (productGrid && this.originalProducts) {
      productGrid.innerHTML = this.originalProducts;
      // Remove variant-view class
      productGrid.classList.remove('variant-view');
    }
    
    // Hide back button and subtitle
    if (backButtonContainer) backButtonContainer.style.display = 'none';
    if (sizeSubtitle) sizeSubtitle.style.display = 'none';
    
    // Hide sort container when in products view
    if (sortContainer) sortContainer.style.display = 'none';
    
    // Restore original title - just show collection name
    if (collectionTitleText && this.currentProduct) {
      // Get original collection name from first filter button
      const firstButton = document.querySelector('.collection-filter-btn[data-filter-type="all"]');
      if (firstButton) {
        // Extract just the collection name (remove "All - " prefix)
        const buttonText = firstButton.textContent.trim();
        const collectionName = buttonText.replace('All - ', '');
        collectionTitleText.textContent = collectionName;
      }
    }
    
    this.currentView = 'products';
    this.currentProduct = null;
    
    // Re-bind product buttons and comparison features
    this.productCards = Array.from(document.querySelectorAll('.product-card'));
    this.bindProductSelectionButtons();
    this.bindCompareButtons();
    
    // Re-apply the current filter if we have one
    if (this.currentFilterButton) {
      const filterType = this.currentFilterButton.getAttribute('data-filter-type');
      const filterValue = this.currentFilterButton.getAttribute('data-filter-value');
      this.applyFilter(filterType, filterValue, this.currentFilterButton);
    }
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async showVariantSelection(productId, productHandle, productCard) {
    console.log('=== Show Variant Selection ===');
    console.log('Product ID:', productId);
    console.log('Product Handle:', productHandle);
    
    const productGrid = document.getElementById('productGrid');
    const collectionTitleText = document.getElementById('collectionTitleText');
    const backButtonContainer = document.getElementById('backButtonContainer');
    const sizeSubtitle = document.getElementById('sizeSelectionSubtitle');
    const sortContainer = document.querySelector('.collection-sort-container');
    
    // Get brand and series from the card
    const brandElement = productCard.querySelector('.product-brand');
    const seriesElement = productCard.querySelector('.product-series-title');
    const brand = brandElement ? brandElement.textContent.trim() : '';
    const series = seriesElement ? seriesElement.textContent.trim() : '';
    
    console.log('Brand:', brand);
    console.log('Series:', series);
    
    // Hide sort container
    if (sortContainer) sortContainer.style.display = 'none';
    
    // Show loading state
    if (productGrid) {
      productGrid.innerHTML = this.createLoadingHTML();
      // Add variant-view class to disable hover
      productGrid.classList.add('variant-view');
    }
    
    try {
      // Fetch product data
      console.log('Fetching product data from:', `/products/${productHandle}.js`);
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const product = await response.json();
      console.log('Product data received:', product);
      
      this.currentProduct = { brand, series, handle: productHandle };
      
      // Update title with brand and series
      if (collectionTitleText) {
        let titleText = '';
        if (brand && series) {
          titleText = `${brand} - ${series}`;
        } else if (series) {
          titleText = series;
        } else {
          titleText = product.title;
        }
        collectionTitleText.textContent = titleText;
      }
      
      // Show back button and subtitle
      if (backButtonContainer) backButtonContainer.style.display = 'block';
      if (sizeSubtitle) sizeSubtitle.style.display = 'block';
      
      // Find size option and get variants for each size
      let sizeVariants = [];
      
      // Extract dimensions from variant title to group by size
      const extractDimensions = (title) => {
        if (!title) return null;
        
        // Match pattern like: DIMENSIONS: 60.5"(H) X 30"(W) X 27.5"(D*)
        const dimensionsMatch = title.match(/DIMENSIONS:\s*([^/]+)/i);
        if (dimensionsMatch) {
          return dimensionsMatch[1].trim();
        }
        
        // Try to extract just the size dimensions if in different format
        const sizeMatch = title.match(/(\d+\.?\d*"\s*\(H\)\s*[xX×]\s*\d+\.?\d*"\s*\(W\)\s*[xX×]\s*\d+\.?\d*"\s*\(D[^)]*\))/i);
        if (sizeMatch) {
          return sizeMatch[1].trim();
        }
        
        // Try to extract size from option values (e.g., "Small", "Medium", "Large")
        const sizeOptionMatch = title.match(/^(Small|Medium|Large|Extra Large|XL|XXL|\d+\.?\d*"|\d+\/\d+")/i);
        if (sizeOptionMatch) {
          return sizeOptionMatch[1].trim();
        }
        
        return null;
      };
      
      if (product.variants && product.variants.length > 0) {
        // Group variants by size/dimensions
        const sizeMap = new Map();
        
        product.variants.forEach(variant => {
          if (variant && variant.title && variant.title !== 'Default Title') {
            const dimensions = extractDimensions(variant.title);
            
            // If we found dimensions/size, use them as the key
            if (dimensions) {
              // Only add the first variant for each size
              if (!sizeMap.has(dimensions)) {
                sizeMap.set(dimensions, {
                  ...variant,
                  dimensions: dimensions
                });
              }
            } else {
              // No dimensions found, try to use option values
              if (product.options && product.options.length > 0) {
                // Look for size-related option names
                const sizeOptionIndex = product.options.findIndex(option => {
                  const optionName = typeof option === 'string' ? option : option.name || option;
                  return optionName.toLowerCase().includes('size') || 
                         optionName.toLowerCase().includes('dimension');
                });
                
                if (sizeOptionIndex >= 0 && variant.options && variant.options[sizeOptionIndex]) {
                  const sizeValue = variant.options[sizeOptionIndex];
                  if (!sizeMap.has(sizeValue)) {
                    sizeMap.set(sizeValue, {
                      ...variant,
                      dimensions: sizeValue
                    });
                  }
                } else {
                  // Last resort: use full title as fallback
                  if (!sizeMap.has(variant.title)) {
                    sizeMap.set(variant.title, {
                      ...variant,
                      dimensions: variant.title
                    });
                  }
                }
              } else {
                // No options, use full title as fallback
                if (!sizeMap.has(variant.title)) {
                  sizeMap.set(variant.title, {
                    ...variant,
                    dimensions: variant.title
                  });
                }
              }
            }
          }
        });
        
        sizeVariants = Array.from(sizeMap.values());
      }
      
      // Debug log
      console.log('Product options:', product.options);
      console.log('Product options structure:', product.options.map(opt => ({
        type: typeof opt,
        value: opt,
        name: opt.name || opt
      })));
      console.log('Total variants:', product.variants ? product.variants.length : 0);
      console.log('Unique size variants:', sizeVariants.length);
      console.log('Size variants:', sizeVariants.map(v => ({
        dimensions: v.dimensions,
        title: v.title,
        id: v.id
      })));
      
      // Log each variant for debugging
      if (product.variants) {
        console.log('All variants breakdown:');
        product.variants.forEach((variant, index) => {
          console.log(`Variant ${index + 1}:`, {
            title: variant.title,
            options: variant.options,
            id: variant.id
          });
        });
      }
      
      if (sizeVariants.length === 0) {
        // No variants, go directly to product page
        console.log('No variants found, redirecting to product page');
        window.location.href = `/products/${productHandle}`;
        return;
      }
      
      // Build variant grid
      this.buildVariantGrid(sizeVariants, product, productGrid);
      this.currentView = 'variants';
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error loading product variants:', error);
      if (productGrid) {
        productGrid.innerHTML = '<div class="variant-error">Error loading variants. Please try again.</div>';
      }
    }
  }

  // AMSEC/Liberty configurator products carry no real Shopify variants — their sizes live in
  // model_size_configuration metaobjects and were embedded on the card as
  // data-configurator-sizes JSON by the Liquid template. Builds the same inline size-picker
  // UI as showVariantSelection() but from that embedded data instead of a /products/{handle}.js
  // fetch, and links each size to the PDP with ?size=<code> so the configurator section
  // (product-configurator-liberty.js / -amsec.js) preselects that size instead of starting blank.
  showConfiguratorSizeSelection(productHandle, productCard) {
    const productGrid = document.getElementById('productGrid');
    const collectionTitleText = document.getElementById('collectionTitleText');
    const backButtonContainer = document.getElementById('backButtonContainer');
    const sizeSubtitle = document.getElementById('sizeSelectionSubtitle');
    const sortContainer = document.querySelector('.collection-sort-container');

    const brandElement = productCard.querySelector('.product-brand');
    const seriesElement = productCard.querySelector('.product-series-title');
    const brand = brandElement ? brandElement.textContent.trim() : '';
    const series = seriesElement ? seriesElement.textContent.trim() : '';

    let sizes = [];
    try {
      sizes = JSON.parse(productCard.getAttribute('data-configurator-sizes') || '[]');
    } catch (error) {
      console.error('Error parsing configurator sizes:', error);
    }

    if (!sizes.length) {
      window.location.href = `/products/${productHandle}`;
      return;
    }

    if (sortContainer) sortContainer.style.display = 'none';

    this.currentProduct = { brand, series, handle: productHandle };

    if (collectionTitleText) {
      collectionTitleText.textContent = brand && series ? `${brand} - ${series}` : (series || productHandle);
    }

    if (backButtonContainer) backButtonContainer.style.display = 'block';
    if (sizeSubtitle) sizeSubtitle.style.display = 'block';

    const imageWrapper = productCard.querySelector('.product-image-wrapper');
    const imageUrl = imageWrapper ? imageWrapper.querySelector('.product-image-primary')?.src : '';

    let html = '';
    sizes.forEach(size => {
      // model_size_configuration's base_price is stored in plain dollars (matches the
      // configurator's own money() helper), unlike variant.price which is in cents —
      // multiply by 100 before reusing formatMoney().
      const price = this.formatMoney((size.price || 0) * 100);
      const sizeParam = encodeURIComponent(size.code || '');

      // Real per-size photo (standard build: chrome hardware, ESL10 lock, 3-spoke handle,
      // right hinge, logo on — see sm-collection-template.liquid) when the metaobjects have
      // one, layered body+hardware+logo exactly like the PDP configurator's .lc__hero-layer
      // stack. Falls back to the single generic product image (old behavior, same image for
      // every size) only when no match exists for this size — e.g. Liberty models, which
      // don't carry the AMSEC-only hardware/lock/handle/hinge layer axes this match needs.
      const sizeImageUrl = size.bodyImageUrl || imageUrl;
      const imageMarkup = sizeImageUrl
        ? `<img class="product-image-primary product-image-layer product-image-body" src="${sizeImageUrl}" alt="${this.escapeHTML(size.name || '')}" loading="lazy" />`
          + (size.hardwareImageUrl ? `<img class="product-image-layer product-image-hardware" src="${size.hardwareImageUrl}" alt="" loading="lazy" />` : '')
          + (size.logoImageUrl ? `<img class="product-image-layer product-image-logo" src="${size.logoImageUrl}" alt="" loading="lazy" />` : '')
        : '<div class="product-no-image">No Image</div>';

      // Size-type content so shoppers can tell sizes apart at a glance — exterior dimensions
      // and gun capacity from the size's model_size_configuration. Each row is omitted when
      // that size has no value; the whole block is omitted when it has neither.
      const extDims = (size.exteriorDimensions || '').trim();
      const gunCap = String(size.gunCapacity || '').trim();
      const gunCapText = /^\d+$/.test(gunCap) ? `Up to ${gunCap} guns` : gunCap;
      const sizeSpecsMarkup = (extDims || gunCapText)
        ? `<dl class="product-size-specs">`
          + (extDims ? `<div class="product-size-spec"><dt>Exterior Dimensions</dt><dd>${this.escapeHTML(extDims)}</dd></div>` : '')
          + (gunCapText ? `<div class="product-size-spec"><dt>Gun Capacity</dt><dd>${this.escapeHTML(gunCapText)}</dd></div>` : '')
          + `</dl>`
        : '';

      html += `
        <div class="product-card">
          <a href="/products/${productHandle}?size=${sizeParam}" class="product-card-link" aria-label="View ${this.escapeHTML(size.name || '')}"></a>
          <div class="product-image-wrapper">
            ${imageMarkup}
          </div>
          <div class="product-info">
            <div class="product-dimensions">${this.escapeHTML(size.name || '')}</div>
            ${sizeSpecsMarkup}
            <div class="product-price-wrapper">
              <span class="product-price-range">From ${price}</span>
            </div>
            <a href="/products/${productHandle}?size=${sizeParam}" class="product-cta-btn">Configure</a>
          </div>
        </div>
      `;
    });

    if (productGrid) {
      productGrid.innerHTML = html;
      productGrid.classList.add('variant-view');
    }

    this.currentView = 'variants';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  createLoadingHTML() {
    let html = '';
    const loadCount = this.columnsPerRow * 2; // Show 2 rows
    for (let i = 0; i < loadCount; i++) {
      html += `
        <div class="skeleton-card">
          <div class="skeleton-header">
            <div class="skeleton skeleton-brand"></div>
            <div class="skeleton skeleton-title"></div>
          </div>
          <div class="skeleton skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-price"></div>
            <div class="skeleton skeleton-button"></div>
          </div>
        </div>
      `;
    }
    return html;
  }

  buildVariantGrid(variants, product, container) {
    console.log('Building variant grid with', variants.length, 'variants');
    
    // Get brand and model from current product
    const brand = this.currentProduct?.brand || '';
    const series = this.currentProduct?.series || product.title;
    
    // Get product metafields for specs
    const securityLevelRaw = product.metafields?.specs?.security_brand_level;
    const fireRating = product.metafields?.specs?.fire_rating_minutes;
    
    // Extract just the number from security level
    const securityLevelData = this.extractSecurityLevel(securityLevelRaw);
    const securityLevelDisplay = securityLevelData.level > 0 ? securityLevelData.display : '';
    
    let html = '';
    
    variants.forEach((variant, index) => {
      try {
        const image = variant.featured_image || product.featured_image || (product.images && product.images[0]);
        const secondImage = product.images && product.images[1];
        const imageUrl = image ? (image.src || image) : '';
        const secondImageUrl = secondImage ? (secondImage.src || secondImage) : '';
        
        // Use the extracted dimensions or fall back to title
        const dimensions = variant.dimensions || variant.title || 'Size Variant';
        
        const price = this.formatMoney(variant.price || 0);
        const compareAtPrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;
        const available = variant.available !== false; // Default to true if not specified
        const onSale = compareAtPrice && variant.compare_at_price > variant.price;
        
        console.log(`Variant ${index + 1}:`, {
          dimensions,
          price: variant.price,
          compareAtPrice: variant.compare_at_price,
          available,
          onSale,
          hasImage: !!imageUrl
        });
      
      html += `
        <div class="product-card ${!available ? 'product-unavailable' : ''}">
          ${available ? `<a href="/products/${product.handle}?variant=${variant.id}" class="product-card-link" aria-label="View ${dimensions}"></a>` : ''}
          ${onSale ? '<div class="sale-badge">Sale</div>' : ''}
          
          <div class="product-image-wrapper">
            ${imageUrl ? `
              <img 
                class="product-image-primary"
                src="${imageUrl}" 
                alt="${dimensions}" 
                loading="lazy" 
              />
              ${secondImageUrl ? `
                <img 
                  class="product-image-secondary"
                  src="${secondImageUrl}" 
                  alt="${dimensions}" 
                  loading="lazy" 
                />
              ` : ''}
            ` : '<div class="product-no-image">No Image</div>'}
          </div>

          <div class="product-info">
            <div class="product-specs">
              ${securityLevelDisplay ? `• ${securityLevelDisplay} •` : ''}
              ${fireRating && fireRating != 0 ? `${fireRating} Minute Fire Protection` : ''}
            </div>

            <div class="product-dimensions">${dimensions}</div>
            <div class="product-price-wrapper">
              ${onSale ? `<span class="product-compare-price">${compareAtPrice}</span>` : ''}
              <span class="product-price-range ${onSale ? 'product-sale-price' : ''}">${price}</span>
            </div>
            ${this.mapNoticeEnabled && this.mapInlineMessage ? `<p class="map-inline-note">${this.escapeHTML(this.mapInlineMessage)}</p>` : ''}
            ${available 
              ? `<a href="/products/${product.handle}?variant=${variant.id}" class="product-cta-btn">Shop Now</a>` 
              : `<button class="product-cta-btn product-cta-disabled" disabled>Out of Stock</button>`
            }
          </div>
        </div>
      `;
      } catch (error) {
        console.error(`Error building variant card ${index + 1}:`, error);
        // Continue with next variant
      }
    });
    
    console.log('Variant grid HTML generated, length:', html.length);
    
    if (container) {
      container.innerHTML = html;

      // Ensure sale badges are visible after DOM update
      setTimeout(() => {
        const saleBadges = container.querySelectorAll('.sale-badge');
        saleBadges.forEach(badge => {
          badge.style.display = 'block';
          badge.style.opacity = '1';
          badge.style.visibility = 'visible';
          badge.style.zIndex = '100';
          badge.style.position = 'absolute';
          badge.style.top = '0.75rem';
          badge.style.left = '0.75rem';
        });
        console.log('Ensured visibility for', saleBadges.length, 'sale badges');
      }, 100);
    } else {
      console.error('Container element not found!');
    }
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    // Add comma for thousands separator
    return `$${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  escapeHTML(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ========== COMPARISON METHODS ==========
  
  initializeComparison() {
    console.log('Initializing comparison features');
    
    // Bind compare buttons
    this.bindCompareButtons();
    
    // Bind comparison UI elements
    const compareProductsBtn = document.getElementById('compare-products-btn');
    const clearSelectionsBtn = document.getElementById('clear-selections-btn');
    const closeModalBtn = document.getElementById('close-comparison-modal');
    const comparisonModal = document.getElementById('product-comparison-modal');
    
    if (compareProductsBtn) {
      compareProductsBtn.addEventListener('click', () => this.showComparisonModal());
    }
    
    if (clearSelectionsBtn) {
      clearSelectionsBtn.addEventListener('click', () => this.resetComparison());
    }
    
    if (closeModalBtn) {
      closeModalBtn.addEventListener('click', () => this.hideComparisonModal());
    }
    
    if (comparisonModal) {
      comparisonModal.addEventListener('click', (e) => {
        if (e.target === comparisonModal) {
          this.hideComparisonModal();
        }
      });
    }
    
    // Close on Escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && comparisonModal && comparisonModal.classList.contains('visible')) {
        this.hideComparisonModal();
      }
    });
  }

  bindCompareButtons() {
    const compareButtons = document.querySelectorAll('.compare-button');
    
    compareButtons.forEach(button => {
      // Remove existing listeners by cloning
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      newButton.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        const productHandle = newButton.getAttribute('data-product-handle');
        
        if (newButton.classList.contains('active')) {
          this.removeFromComparison(productHandle, newButton);
        } else {
          if (this.selectedProducts.length >= this.MAX_COMPARISON_PRODUCTS) {
            this.showToast(`Maximum ${this.MAX_COMPARISON_PRODUCTS} products can be compared`, 'warning');
            return;
          }
          this.addToComparison(productHandle, newButton);
        }
      });
    });
  }

  addToComparison(productHandle, button) {
    if (!this.selectedProducts.includes(productHandle)) {
      this.selectedProducts.push(productHandle);
      button.classList.add('active');
      this.updateComparisonUI();
      this.showToast(
        this.selectedProducts.length === 1 
          ? 'Product added to comparison. Add more to compare.' 
          : `${this.selectedProducts.length} products selected. ${this.MAX_COMPARISON_PRODUCTS - this.selectedProducts.length} more can be added.`
      );
    }
  }

  removeFromComparison(productHandle, button) {
    const index = this.selectedProducts.indexOf(productHandle);
    if (index > -1) {
      this.selectedProducts.splice(index, 1);
      button.classList.remove('active');
      this.updateComparisonUI();
      
      if (this.selectedProducts.length === 0) {
        this.hideToast();
      } else {
        this.showToast(`Product removed. ${this.selectedProducts.length} product(s) in comparison.`);
      }
    }
  }

  updateComparisonUI() {
    const toast = document.getElementById('comparison-toast');
    const compareBtn = document.getElementById('compare-products-btn');
    
    if (this.selectedProducts.length >= 2 && compareBtn) {
      compareBtn.style.display = 'block';
    } else if (compareBtn) {
      compareBtn.style.display = 'none';
    }
    
    if (this.selectedProducts.length === 0 && toast) {
      toast.classList.remove('visible');
    }
  }

  showToast(message, type = 'info') {
    const toast = document.getElementById('comparison-toast');
    const toastText = document.getElementById('toast-text');
    
    if (toast && toastText) {
      toastText.textContent = message;
      toast.classList.add('visible');
      toast.dataset.type = type;
    }
  }

  hideToast() {
    const toast = document.getElementById('comparison-toast');
    if (toast) {
      toast.classList.remove('visible');
    }
  }

  showComparisonModal() {
    if (this.selectedProducts.length < 2) {
      this.showToast('Please select at least 2 products to compare', 'warning');
      return;
    }
    
    // Hide toast when modal opens
    this.hideToast();
    
    const modal = document.getElementById('product-comparison-modal');
    if (modal) {
      this.populateComparisonContent();
      modal.classList.add('visible');
      modal.dataset.modalState = 'visible';
      document.body.style.overflow = 'hidden';
    }
  }

  hideComparisonModal() {
    const modal = document.getElementById('product-comparison-modal');
    if (modal) {
      modal.classList.remove('visible');
      modal.dataset.modalState = 'hidden';
      document.body.style.overflow = '';
      
      // Don't reset comparison when modal closes - keep selections
    }
  }

  getProductData(handle) {
    const button = document.querySelector(`.compare-button[data-product-handle="${handle}"]`);
    if (!button) return null;
    
    try {
      const metafieldsStr = button.getAttribute('data-metafields');
      return JSON.parse(metafieldsStr);
    } catch (error) {
      console.error('Error parsing product data:', error);
      return null;
    }
  }

  populateComparisonContent() {
    const container = document.getElementById('comparison-sections-container');
    if (!container) return;
    
    const productData = this.selectedProducts.map(handle => this.getProductData(handle)).filter(Boolean);
    
    if (productData.length === 0) return;
    
    let html = '<div class="products-grid">';
    
    // Product cards
    productData.forEach(product => {
      const isOnSale = product.compare_at_price && product.compare_at_price > product.price_cents;
      
      html += `
        <div class="modal-product-card" onclick="window.location.href='${product.url}'">
          <img src="${product.image}" alt="${product.title}" class="modal-product-image">
          <h3 class="modal-product-title">${product.series}</h3>
          <div class="modal-product-brand">${product.brand}</div>
          ${isOnSale ? `
            <div class="product-price-wrapper">
              <span class="product-compare-price">$${(product.compare_at_price / 100).toFixed(2)}</span>
              <span class="product-sale-price">${product.price}</span>
            </div>
          ` : `
            <div class="modal-product-price">${product.price}</div>
          `}
          <a href="${product.url}" class="product-cta-btn" onclick="event.stopPropagation()">View Product</a>
        </div>
      `;
    });
    
    html += '</div>';
    
    // Comparison sections
    html += this.createOverviewSection(productData);
    html += this.createSecuritySection(productData);
    html += this.createFireSection(productData);
    html += this.createPhysicalSection(productData);
    html += this.createFeaturesSection(productData);
    
    // Mobile layout - each product with its specs
    html += '<div class="mobile-comparison-layout">';
    productData.forEach(product => {
      html += this.createMobileProductCard(product);
    });
    html += '</div>';
    
    container.innerHTML = html;
    
    // Reinitialize icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

  createMobileProductCard(product) {
    const securityLevel = this.extractSecurityLevel(product.security_level);
    const fireRating = product.fire_rating && product.fire_rating !== 0 ? product.fire_rating : 0;
    
    return `
      <div class="mobile-product-comparison">
        <div class="mobile-product-header">
          <img src="${product.image}" alt="${product.title}">
          <div class="mobile-product-brand">${product.brand || 'N/A'}</div>
          <div class="mobile-product-title">${product.series || 'N/A'}</div>
          <div class="mobile-product-price">${product.price}</div>
        </div>
        
        <div class="mobile-specs-section">
          <div class="mobile-specs-header">Overview</div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Made in USA</div>
            <div class="mobile-spec-value">${product.made_in_usa ? '✓ Yes' : '✗ No'}</div>
          </div>
        </div>
        
        <div class="mobile-specs-section">
          <div class="mobile-specs-header">Security & Fire</div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Security Level</div>
            <div class="mobile-spec-value">
              ${this.createMobileSecurityIcons(securityLevel)}
            </div>
          </div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Fire Protection</div>
            <div class="mobile-spec-value">
              ${this.createMobileFireIcons(fireRating, product.title)}
            </div>
          </div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Lock Option</div>
            <div class="mobile-spec-value">${product.lock_option || 'N/A'}</div>
          </div>
        </div>
        
        <div class="mobile-specs-section">
          <div class="mobile-specs-header">Physical Specs</div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Long Gun Capacity</div>
            <div class="mobile-spec-value">${product.capacity && product.capacity !== 'N/A' ? `Up to ${product.capacity} guns` : 'None'}</div>
          </div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Weight</div>
            <div class="mobile-spec-value">${product.weight && product.weight !== 'N/A' ? `${product.weight} lbs` : 'None'}</div>
          </div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">External Size</div>
            <div class="mobile-spec-value">${product.external_dimensions && product.external_dimensions !== 'N/A' ? product.external_dimensions : 'None'}</div>
          </div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Internal Size</div>
            <div class="mobile-spec-value">${product.internal_dimensions && product.internal_dimensions !== 'N/A' ? product.internal_dimensions : 'None'}</div>
          </div>
        </div>
        
        <div class="mobile-specs-section">
          <div class="mobile-specs-header">Features</div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Interior Lighting</div>
            <div class="mobile-spec-value">${product.interior_lighting ? '✓ Yes' : '✗ No'}</div>
          </div>
          <div class="mobile-spec-row">
            <div class="mobile-spec-label">Interior Drawers</div>
            <div class="mobile-spec-value">${product.interior_drawers && product.interior_drawers > 0 ? `${product.interior_drawers} drawer${product.interior_drawers > 1 ? 's' : ''}` : 'None'}</div>
          </div>
        </div>
      </div>
    `;
  }

  createMobileSecurityIcons(levelData) {
    if (levelData.level === 0) {
      return `<span class="spec-badge spec-badge--muted">N/A</span>`;
    }
    return `<span class="spec-badge">${levelData.display}</span>`;
  }

  createMobileFireIcons(minutes, title) {
    if (minutes === 0) {
      if (title && title.indexOf('Accessory - ') === 0) return '';
      return `<span class="spec-badge spec-badge--muted">No Fire Protection</span>`;
    }
    return `<span class="spec-badge">${minutes} min</span>`;
  }

  createOverviewSection(products) {
    return `
      <div class="comparison-section">
        <h3>Overview</h3>
        <div class="comparison-grid">
          ${this.createComparisonRow('Brand', products.map(p => p.brand || 'N/A'))}
          ${this.createComparisonRow('Series', products.map(p => p.series || 'N/A'))}
          ${this.createComparisonRow('Price', products.map(p => p.price))}
          ${this.createComparisonRow('Made in USA', products.map(p => p.made_in_usa ? '✓ Yes' : '✗ No'))}
        </div>
      </div>
    `;
  }

  createSecuritySection(products) {
    const securityLevels = products.map(p => this.extractSecurityLevel(p.security_level));
    
    return `
      <div class="comparison-section">
        <h3>Security Features</h3>
        <div class="comparison-grid">
          ${this.createIconComparisonRow('Security Level', securityLevels, 'security')}
          ${this.createComparisonRow('UL Certification', products.map(p => p.security_ul_cert === true ? 'Yes' : p.security_ul_cert === false ? 'No' : 'N/A'))}
        </div>
      </div>
    `;
  }

  createFireSection(products) {
    const fireRatings = products.map(p => ({
      minutes: p.fire_rating && p.fire_rating !== 0 ? p.fire_rating : 0,
      display: p.fire_rating && p.fire_rating !== 0 ? `${p.fire_rating} min` : 'No Fire Protection',
      isAccessory: !!(p.title && p.title.indexOf('Accessory - ') === 0)
    }));
    
    return `
      <div class="comparison-section">
        <h3>Fire Protection</h3>
        <div class="comparison-grid">
          ${this.createFireIconComparisonRow('Fire Rating', fireRatings)}
        </div>
      </div>
    `;
  }

  extractSecurityLevel(securityValue) {
    if (!securityValue || securityValue === 'N/A') {
      return { level: 0, display: 'N/A' };
    }
    
    // Convert to string for consistency
    const valueStr = securityValue.toString().trim();
    
    // Check if it's just a number
    if (!isNaN(valueStr) && valueStr !== '') {
      return { level: parseInt(valueStr), display: `Level ${valueStr}` };
    }
    
    // Check for "Level X Security" or "Level X" format and extract just the number
    const levelMatch = valueStr.match(/Level\s*(\d+)/i);
    if (levelMatch) {
      return { level: parseInt(levelMatch[1]), display: `Level ${levelMatch[1]}` };
    }
    
    // If it contains just a number somewhere, extract it
    const numberMatch = valueStr.match(/(\d+)/);
    if (numberMatch) {
      return { level: parseInt(numberMatch[1]), display: `Level ${numberMatch[1]}` };
    }
    
    return { level: 0, display: 'N/A' };
  }

  createIconComparisonRow(label, securityLevels, type) {
    const values = securityLevels.map(levelData => {
      if (levelData.level === 0) {
        return `<div class="comparison-value no-data">
          <span class="spec-badge spec-badge--muted">N/A</span>
        </div>`;
      }

      return `<div class="comparison-value">
        <span class="spec-badge">${levelData.display}</span>
      </div>`;
    });

    return `
      <div class="comparison-row">
        <div class="comparison-label">${label}</div>
        ${values.join('')}
      </div>
    `;
  }

  createFireIconComparisonRow(label, fireRatings) {
    const values = fireRatings.map(rating => {
      if (rating.minutes === 0) {
        if (rating.isAccessory) {
          return `<div class="comparison-value no-data"></div>`;
        }
        return `<div class="comparison-value no-data">
          <span class="spec-badge spec-badge--muted">No Fire Protection</span>
        </div>`;
      }

      return `<div class="comparison-value">
        <span class="spec-badge">${rating.display}</span>
      </div>`;
    });

    return `
      <div class="comparison-row">
        <div class="comparison-label">${label}</div>
        ${values.join('')}
      </div>
    `;
  }

  createPhysicalSection(products) {
    return `
      <div class="comparison-section">
        <h3>Physical Specifications</h3>
        <div class="comparison-grid">
          ${this.createComparisonRow('Long Gun Capacity', products.map(p => p.capacity && p.capacity !== 'N/A' ? `Up to ${p.capacity} guns` : 'None'))}
          ${this.createComparisonRow('Weight', products.map(p => p.weight && p.weight !== 'N/A' ? `${p.weight} lbs` : 'None'))}
          ${this.createComparisonRow('External Dimensions', products.map(p => p.external_dimensions && p.external_dimensions !== 'N/A' ? p.external_dimensions : 'None'))}
          ${this.createComparisonRow('Internal Dimensions', products.map(p => p.internal_dimensions && p.internal_dimensions !== 'N/A' ? p.internal_dimensions : 'None'))}
        </div>
      </div>
    `;
  }

  createFeaturesSection(products) {
    return `
      <div class="comparison-section">
        <h3>Additional Features</h3>
        <div class="comparison-grid">
          ${this.createComparisonRow('Interior Lighting', products.map(p => p.interior_lighting ? '✓ Yes' : '✗ No'))}
          ${this.createComparisonRow('Interior Drawers', products.map(p => p.interior_drawers && p.interior_drawers > 0 ? `${p.interior_drawers} drawer${p.interior_drawers > 1 ? 's' : ''}` : 'None'))}
        </div>
      </div>
    `;
  }

  createComparisonRow(label, values) {
    const valueCells = values.map(value => `<div class="comparison-value">${value}</div>`).join('');
    return `
      <div class="comparison-row">
        <div class="comparison-label">${label}</div>
        ${valueCells}
      </div>
    `;
  }

  resetComparison() {
    this.selectedProducts = [];
    
    // Remove active state from all compare buttons
    document.querySelectorAll('.compare-button.active').forEach(button => {
      button.classList.remove('active');
    });
    
    this.hideToast();
    this.updateComparisonUI();
  }
}

// Initialize the collection template
const customCollection = new CustomCollectionTemplate();